/**
 * Code QR de connexion d'un reseau Wi-Fi (1Password « Join network », Mots de passe d'Apple « Partager ») : un
 * invite scanne avec l'appareil photo de son telephone et rejoint le reseau sans qu'on lui dicte la cle.
 *
 * Pur, zero E/S : le contenu est lu dans l'entree au moment du rendu et n'est garde nulle part ; le code est dessine
 * sur le poste (`qr-code.ts`), jamais demande a un service.
 *
 * Contenu au format `WIFI:` de ZXing, celui que lisent les appareils photo d'iOS et d'Android :
 *   WIFI:T:<WPA|WEP|nopass>;S:<ssid>;P:<cle>;H:true;;
 * - `\ ; , : "` sont echappes d'une barre oblique inversee ;
 * - un SSID fait seulement de chiffres hexadecimaux (« CAFE1234 ») est mis entre guillemets, faute de quoi un lecteur
 *   le prendrait pour des octets en hexadecimal ; une cle aussi, SAUF si elle a la longueur d'une cle brute (64
 *   hexadecimaux en WPA, 10 ou 26 en WEP) : c'est alors bien une cle brute, la citer en ferait une phrase de passe ;
 * - WPA, WPA2 et WPA3 Personnel s'ecrivent `T:WPA` : c'est la seule valeur que tous les appareils photo connaissent,
 *   et un reseau WPA3 l'accepte (le telephone negocie SAE de lui-meme).
 */
import type { EntrySecrets } from '@klavox/crypto-core';
import { QR_MAX_BYTES } from './qr-code.js';
import { readTypedFields, wifiSecurity, wifiSecurityLabel, type WifiSecurity } from './typed-fields.js';

export type WifiQrContent =
  | {
      readonly ok: true;
      /** Le texte a coder : il contient la cle du reseau, a ne jamais ecrire dans un attribut ni un journal. */
      readonly payload: string;
      readonly ssid: string;
      /** Securite telle qu'on l'affiche sous le code (« WPA2 Personnel », « Aucune (reseau ouvert) »…). */
      readonly securityLabel: string;
      /** Securite non renseignee dans l'entree, mais une cle presente : WPA est suppose, et on le dit. */
      readonly assumed: boolean;
      readonly hidden: boolean;
    }
  | { readonly ok: false; readonly problem: string };

function escapeWifiValue(value: string): string {
  return value.replace(/[\\;,:"]/gu, (char) => `\\${char}`);
}

const HEX = /^[0-9a-f]+$/iu;

function isRawKey(type: 'WPA' | 'WEP', key: string): boolean {
  if (!HEX.test(key)) return false;
  return type === 'WPA' ? key.length === 64 : key.length === 10 || key.length === 26;
}

/** Contenu du code de connexion de l'entree, ou ce qui manque pour le faire. */
export function wifiQrContent(secrets: EntrySecrets): WifiQrContent {
  const fields = readTypedFields(secrets, 'wifi');
  const ssid = fields['ssid'] ?? '';
  if (ssid.trim().length === 0) return { ok: false, problem: 'Renseignez le nom du reseau (SSID) pour obtenir son code QR.' };
  const password = typeof secrets.password === 'string' ? secrets.password : '';
  const security = wifiSecurity(fields['security'] ?? '');
  const hidden = fields['hidden'] === 'true';
  const open = security === 'unsecured' || (security === null && password.length === 0);
  if (!open && password.length === 0) return { ok: false, problem: 'Ajoutez le mot de passe du reseau pour obtenir son code QR.' };

  const type = open ? 'nopass' : security === 'wep' ? 'WEP' : 'WPA';
  const quoted = (value: string): string => (HEX.test(value) ? `"${escapeWifiValue(value)}"` : escapeWifiValue(value));
  let payload = `WIFI:T:${type};S:${quoted(ssid)};`;
  if (type !== 'nopass') payload += `P:${isRawKey(type, password) ? password : quoted(password)};`;
  if (hidden) payload += 'H:true;';
  payload += ';';
  if (new TextEncoder().encode(payload).length > QR_MAX_BYTES) {
    return { ok: false, problem: 'Nom du reseau et mot de passe trop longs pour un code QR de connexion.' };
  }
  return {
    ok: true,
    payload,
    ssid,
    securityLabel: open ? wifiSecurityLabel('unsecured') : security === null ? 'WPA (suppose)' : wifiSecurityLabel(security),
    assumed: !open && security === null,
    hidden,
  };
}

/** Reseau lu dans le texte d'un code `WIFI:` (`parseWifiQr`), ou ce qui empeche de le reprendre. */
export type WifiQrImport =
  | {
      readonly ok: true;
      readonly ssid: string;
      /** La cle du reseau, vide pour un reseau ouvert : a ne jamais ecrire dans un attribut ni un journal. */
      readonly password: string;
      /** `''` pour `T:WPA` : la famille WPA sans la version (WPA, WPA2 ou WPA3), que le code ne dit pas. */
      readonly security: WifiSecurity | '';
      readonly hidden: boolean;
    }
  | { readonly ok: false; readonly problem: string };

/** Le texte se donne-t-il pour un code `WIFI:` ? (Il peut encore etre illisible : `parseWifiQr` le dit.) */
export function looksLikeWifiQr(text: string): boolean {
  return /^\s*WIFI:/iu.test(text);
}

type WifiQrChar = { readonly char: string; readonly escaped: boolean };

/** Champs `CLE:valeur` separes par un `;` non echappe ; `\x` vaut `x` et ne separe ni ne cite rien. */
function wifiQrFields(body: string): Map<string, string> {
  const fields = new Map<string, string>();
  let current: WifiQrChar[] = [];
  const flush = (): void => {
    const colon = current.findIndex((c) => c.char === ':' && !c.escaped);
    if (colon > 0) {
      const key = current.slice(0, colon).map((c) => c.char).join('').trim().toUpperCase();
      let value = current.slice(colon + 1);
      const quote = (c: WifiQrChar | undefined): boolean => c !== undefined && c.char === '"' && !c.escaped;
      if (value.length >= 2 && quote(value[0]) && quote(value[value.length - 1])) value = value.slice(1, -1);
      // Premier champ garde : un code forge qui repete `P:` ne remplace pas la cle annoncee.
      if (!fields.has(key)) fields.set(key, value.map((c) => c.char).join(''));
    }
    current = [];
  };
  const chars = [...body];
  for (let i = 0; i < chars.length; i += 1) {
    const char = chars[i]!;
    if (char === '\\' && i + 1 < chars.length) {
      i += 1;
      current.push({ char: chars[i]!, escaped: true });
    } else if (char === ';') {
      flush();
    } else {
      current.push({ char, escaped: false });
    }
  }
  flush();
  return fields;
}

/**
 * Reseau d'un texte de code QR Wi-Fi (`WIFI:T:WPA;S:Box;P:cle;H:true;;`), celui qu'affiche l'appareil photo d'un
 * telephone ou un lecteur de codes devant l'etiquette d'une box : l'inverse de `wifiQrContent`. Les echappements et
 * guillemets sont ceux de ZXing ; une cle brute (64 hexadecimaux) est gardee telle quelle. Un reseau Entreprise
 * (EAP : identifiant, methode, certificat) n'est pas repris : le champ ne saurait pas le garder.
 */
export function parseWifiQr(text: string): WifiQrImport {
  const trimmed = text.trim();
  if (!looksLikeWifiQr(trimmed)) return { ok: false, problem: 'Ce texte n’est pas celui d’un code QR Wi-Fi (WIFI:…).' };
  if (new TextEncoder().encode(trimmed).length > QR_MAX_BYTES) return { ok: false, problem: 'Texte trop long pour venir d’un code QR Wi-Fi.' };
  const fields = wifiQrFields(trimmed.slice('WIFI:'.length));
  const type = (fields.get('T') ?? '').trim().toUpperCase();
  if (type.includes('EAP') || fields.has('E') || fields.has('I') || fields.has('PH2')) {
    return { ok: false, problem: 'Code d’un reseau Wi-Fi Entreprise (EAP) : identifiant et methode ne sont pas repris, saisissez ce reseau a la main.' };
  }
  const ssid = fields.get('S') ?? '';
  if (ssid.trim().length === 0) return { ok: false, problem: 'Ce code Wi-Fi ne porte pas de nom de reseau.' };
  const key = fields.get('P') ?? '';
  let security: WifiSecurity | '';
  if (type === 'NOPASS' || (type === '' && key.length === 0)) security = 'unsecured';
  else if (type === 'WEP') security = 'wep';
  else if (type === 'WPA3' || type === 'SAE') security = 'wpa3-personal';
  else if (type === 'WPA2') security = 'wpa2-personal';
  else if (type === 'WPA' || type === '') security = '';
  else return { ok: false, problem: `Securite « ${type} » inconnue dans ce code Wi-Fi.` };
  const password = security === 'unsecured' ? '' : key;
  if (security !== 'unsecured' && password.length === 0) return { ok: false, problem: 'Ce code Wi-Fi annonce un reseau protege mais ne porte pas sa cle.' };
  const hidden = ['TRUE', '1'].includes((fields.get('H') ?? '').trim().toUpperCase());
  return { ok: true, ssid, password, security, hidden };
}
